/*----------------------------------
- MARKET MENU CATALOG (SSOT)
-----------------------------------*/

import type { IconName } from '@uniquedomains/components/ui/icon';
import { EDITION_PAGES } from '@uniquedomains/components/contracts/editions/public';
import { DOMAINING_NEWS_INDEX_PATH } from '@uniquedomains/components/contracts/news/public';
import { LANDING_WEBSITE_PAGE_ROUTES } from '@/common/catalogs/landing/publicPages';
import type { LandingMenuGroup } from './menuPanels';

/*----------------------------------
- TYPES
-----------------------------------*/

/**
 * A header Market row. Same two-state shape as the Tools catalog: a row either links
 * to a page that exists (`href`) or announces itself as unbuilt (`status`), never both.
 *
 * A row carries no note at its right: the cadence lines ("Updated each night") left with the
 * mega menu redraw of 2026-11-02 (mock parity web-recheck-02), where each column opens on its
 * group title instead.
 */
export type LandingMarketMenuItem = {
    id: string;
    label: string;
    description: string;
    iconName: IconName;
} & (
    | { href: string; status?: undefined }
    | { href?: undefined; status: 'Coming soon' }
);

/*----------------------------------
- CATALOG
-----------------------------------*/

/**
 * The Market menu in its three groups, the one order the desktop panel and the phone sheet both read
 * (mock parity web-recheck-02, Gaetan's decision E2 = B of 2026-11-03: the sheet follows the desktop).
 */
export const LANDING_MARKET_MENU_GROUPS: readonly LandingMenuGroup<LandingMarketMenuItem>[] = [
    {
        id: 'prices-and-sales',
        title: 'Prices and sales',
        items: [
            {
                id: 'sales',
                label: 'Sales',
                description: 'Reported domain sales, by price, ending and word, updated each night.',
                iconName: 'dollar',
                href: LANDING_WEBSITE_PAGE_ROUTES.sales,
            },
            {
                id: 'auctions',
                label: 'Auctions',
                description: 'One-word names at auction on seven venues, closing today.',
                iconName: 'coins',
                href: LANDING_WEBSITE_PAGE_ROUTES.auctions,
            },
            // The registrar hub (ADR-0079).
            {
                id: 'registrars',
                label: 'Registrars',
                description: 'Year 1, renewal and transfer-in prices at nine registrars, ending by ending.',
                iconName: 'tag',
                href: LANDING_WEBSITE_PAGE_ROUTES.registrars,
            },
        ],
    },
    {
        id: 'endings-and-dates',
        title: 'Endings and dates',
        items: [
            {
                id: 'tlds',
                label: 'TLDs',
                description: 'Registry status, prices, sales and launch dates: one page per public-set TLD, the agenda for the rest.',
                iconName: 'globe',
                href: LANDING_WEBSITE_PAGE_ROUTES.tldHub,
            },
            {
                id: 'agenda',
                label: 'Agenda',
                description: 'Launch dates, registry changes, conferences and policy dates.',
                iconName: 'calendar',
                href: LANDING_WEBSITE_PAGE_ROUTES.agenda,
            },
        ],
    },
    {
        id: 'daily-editions',
        title: 'Daily editions',
        items: [
            {
                id: 'news',
                label: 'News',
                description: 'The industry’s day in one summary, with links to the original reporting.',
                iconName: 'feed',
                href: DOMAINING_NEWS_INDEX_PATH,
            },
            // The daily editions (ADR-0074) replace "Trends" (coming soon) and "Opportunities".
            {
                id: 'trending-domains',
                label: EDITION_PAGES.investor.name,
                description: 'The trends rising in search today and the one-word names tied to them.',
                iconName: 'trending-up',
                href: LANDING_WEBSITE_PAGE_ROUTES.trendingDomains,
            },
            {
                id: 'business-ideas',
                label: EDITION_PAGES.founder.name,
                description: 'A business to build on each trending name, with what the name costs.',
                iconName: 'sparkles',
                href: LANDING_WEBSITE_PAGE_ROUTES.businessIdeas,
            },
        ],
    },
];

/** Every Market row in the groups' order. */
export const LANDING_MARKET_MENU_ITEMS: readonly LandingMarketMenuItem[] = LANDING_MARKET_MENU_GROUPS.flatMap((group) => group.items);

/**
 * The pages the Market trigger lights up on, derived from the rows that have one, so
 * an unbuilt row can never light the bar on a page it does not own.
 */
export const LANDING_MARKET_MENU_ACTIVE_PATH_PREFIXES: readonly string[] = LANDING_MARKET_MENU_ITEMS.reduce<
    string[]
>((paths, item) => (item.href ? [...paths, item.href] : paths), []);
